///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
    ConfigPropertiesDTO,
    EngineProductDTO,
    ExtendedProductdata,
    GtthKeyValueDTO,
    ProductMhTypeEnum,
    ProductRadioDTO,
    ProductTypeEnum
} from '../../../../generated/shared-types.model';
import { GtthLogicalTopology } from '../../../../project-view-panel/logical-topology/entities/logical-topology';
import { GtthEntityNameValidation } from '../../../../shared/directives/validation-functions/entity-name';
import { GtthProductNameLinkDirective } from '../../../../shared/directives/style-directives/product-name-link.directive';
import { isDefined } from '@siklu/sui-lib/utils';
import { SuiTranslatePipe } from '@siklu/sui-lib';
import { MatDialogModule } from '@angular/material/dialog';
import { ContextSensitiveHelpComponent } from '../../../../shared/context-sensitive-help/ctx-sensitive-help.component';
import { GtthStyleDirectivesModule } from '../../../../shared/directives/style-directives/style-directives.module';
import { GtthDisableFormControlsDirective } from '../../../../shared/directives/validation-directives/disable-form-controls.directive';

@Component({
    selector: 'winde-bom-select-product',
    templateUrl: './bom-products-select.component.html',
    styleUrls: ['./bom-products-select.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SuiTranslatePipe,
        ReactiveFormsModule,
        MatDialogModule,
        ContextSensitiveHelpComponent,
        GtthStyleDirectivesModule,
        GtthDisableFormControlsDirective
    ]
})
export class GtthBOMProductsSelectComponent implements OnInit {
    @Input() mhProducts: ProductRadioDTO[];
    @Input() viewInReadOnly: boolean;
    @Input() logicalTopology: ConfigPropertiesDTO;
    @Input() productsIsValid: boolean;
    @Input() engineRadioList: EngineProductDTO[];
    @Output() productsIsValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    public editForm: FormGroup;
    public products: FormArray = this.formBuilder.array([]);
    public hasMhProduct: boolean;

    public availableAntennas: any[] = [];
    public availableBu: GtthKeyValueDTO[] = [];
    public availableTu: GtthKeyValueDTO[] = [];
    public firstProductOfLinkList: EngineProductDTO[] = [];
    public secondLinkProductsList: any[] = [];

    public defaultBUName: string;
    public defaultTUName: string;

    private isProductChanged = false;

    constructor(private readonly formBuilder: FormBuilder) {}

    ngOnInit() {
        this.firstProductOfLinkList = this.engineRadioList.filter((radio: EngineProductDTO) => !radio.isTU);
        this.firstProductOfLinkList.forEach((radio: EngineProductDTO) => {
            this.updateSecondProductList(radio); // init options for the second combo-box
        });

        this.engineRadioList.forEach((radio: EngineProductDTO) => {
            this.availableAntennas[radio.id] ??= [];
            this.availableAntennas[radio.id] = radio.pdbConfig.antennas;
            this.availableAntennas[radio.id].sort((antenna1, antenna2) => antenna1.id - antenna2.id);
        });

        if (this.mhProducts) {
            this.mhProducts.forEach((mhRadio: ProductRadioDTO) => {
                if (mhRadio.mhType === ProductMhTypeEnum.BU) {
                    this.availableBu.push({ key: mhRadio.id, value: mhRadio.name } as GtthKeyValueDTO);
                } else {
                    this.availableTu.push({ key: mhRadio.id, value: mhRadio.name } as GtthKeyValueDTO);
                }
            });
        }

        this.initForm();
        this.updateLogicalTopologyWithSelectedProducts();
    }

    getLinkNameByUsedProd(productId: number): string {
        if (this.engineRadioList && isDefined(productId)) {
            const radio = this.engineRadioList.find((product: EngineProductDTO) => product.id === productId);
            if (radio) {
                return (radio.name === 'MultiHaul' ? 'MH-BU' : radio.name) + '_link';
            }
        }
        return '';
    }

    initForm() {
        const selectedProducts = this.logicalTopology.selectedProductsPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX];
        const itemsCount = 4;

        this.getProductDataList(selectedProducts, itemsCount).forEach((item: ExtendedProductdata) => {
            if (item.usedProd === -1) {
                item.usedProd = null;
            }
            if (item.usedProdTU === -1) {
                item.usedProdTU = null;
            }

            item.bomLinkName = item.bomLinkName ? item.bomLinkName : this.getLinkNameByUsedProd(item.usedProd);

            if (isDefined(item.usedProd)) {
                const selectedAnt = this.availableAntennas[item.usedProd].find((antennaItem) => {
                    return antennaItem.id === item.usedAnt0;
                });
                if (!selectedAnt) {
                    item.usedAnt0 = this.availableAntennas[item.usedProd][0].id;
                }
            }

            if (isDefined(item.usedProdTU)) {
                const selectedAnt1 = this.availableAntennas[item.usedProdTU].find((antennaItem) => {
                    return antennaItem.id === item.usedAnt1;
                });
                if (!selectedAnt1) {
                    item.usedAnt1 = this.availableAntennas[item.usedProdTU][0].id;
                }
            }

            const productGroup = this.formBuilder.group({
                bomLinkName: this.formBuilder.control(
                    item.bomLinkName,
                    GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)
                ),
                usedProd: this.formBuilder.control(item.usedProd),
                usedProdTU: this.formBuilder.control(item.usedProdTU),
                usedAnt0: this.formBuilder.control(item.usedAnt0),
                usedAnt1: this.formBuilder.control(item.usedAnt1)
            });
            this.products.push(productGroup);
        });

        this.editForm = this.formBuilder.group({
            productsToUse: this.products,
            defaultMHProducts: this.formBuilder.group({
                defaultBuProductPerLayer: this.formBuilder.control({
                    value: this.logicalTopology.defaultBuProductPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX],
                    disabled: this.viewInReadOnly
                }),
                defaultTuProductPerLayer: this.formBuilder.control({
                    value: this.logicalTopology.defaultTuProductPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX],
                    disabled: this.viewInReadOnly
                })
            })
        });

        this.checkMhProduct();
        this.setProductForInfoButton();
    }

    updateDefaultProductsPerLayer() {
        const defaultMHProducts = this.editForm.get('defaultMHProducts').value;
        Object.keys(defaultMHProducts).forEach((key: string) => {
            this.logicalTopology[key][GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] = defaultMHProducts[key];
            this.setProductForInfoButton();
        });
    }

    checkSelectedProduct(product: AbstractControl) {
        const usedProd = product.get('usedProd').value;
        product.get('bomLinkName').setValue(this.getLinkNameByUsedProd(usedProd));
        if (usedProd !== null) {
            const engineProduct = this.engineRadioList.find((radio) => radio.id === usedProd);
            product.get('usedAnt0').setValue(this.availableAntennas[usedProd][0].id);
            this.updateSecondProductList(engineProduct);
            if (this.hasToShowTUProduct(usedProd)) {
                product.get('usedProdTU').setValue(usedProd);
                product.get('usedAnt1').setValue(this.availableAntennas[usedProd][0].id);
            } else {
                product.get('usedProdTU').setValue(null);
                product.get('usedAnt1').setValue(null);
            }
        } else {
            product.get('usedProdTU').setValue(null);
            product.get('usedAnt1').setValue(null);
        }
        this.checkMhProduct();
        this.isProductChanged = true;
        this.updateLogicalTopologyWithSelectedProducts();
    }

    isMhProduct(productId: number): boolean {
        const engineProduct = this.engineRadioList.find((radio) => radio.id === productId);
        return engineProduct.productType === ProductTypeEnum.MH || engineProduct.productType === ProductTypeEnum.TG_MH;
    }

    hasToShowTUProduct(productId: number): boolean {
        const engineProduct = this.engineRadioList.find((radio) => radio.id === productId);
        return engineProduct && engineProduct.productType === ProductTypeEnum.TG_MH && !engineProduct.isPtP;
    }

    getLink(name: string): string {
        return GtthProductNameLinkDirective.getMHProductLink(name);
    }

    checkLinkNameValidity() {
        const map = new Map<string, AbstractControl>();
        this.products.controls.forEach((formGroup: AbstractControl) => {
            const linkNameControl = formGroup.get('bomLinkName');
            const usedProd = formGroup.get('usedProd').value;
            const hasProduct = usedProd !== null && usedProd > -1;

            if (!linkNameControl.value && hasProduct) {
                linkNameControl.setErrors({ required: true });
            } else if (linkNameControl.value) {
                linkNameControl.setValue(linkNameControl.value.trim());
                if (map.has(linkNameControl.value)) {
                    linkNameControl.setErrors({ sameLinkName: true });
                    map.get(linkNameControl.value).setErrors({ sameLinkName: true });
                } else {
                    map.set(linkNameControl.value, linkNameControl);
                    linkNameControl.setErrors(null);
                }
            }
        });
        this.productsIsValidChange.emit(this.editForm.valid);
    }

    updateLogicalTopologyWithSelectedProducts(): void {
        this.resetLinkNamePagination();

        const linkNames = this.extractLinkNames();
        const paginatedLinkNames = this.addLinkNamePagination(linkNames);
        this.updateFormControlsWithPaginatedNames(paginatedLinkNames);

        const selectedProducts = this.createSelectedProductsDataArray(paginatedLinkNames);
        this.updateLogicalTopologyData(selectedProducts);
        this.emitFormValidityChange();
        this.resetProductChangedFlag();
    }

    private resetLinkNamePagination(): void {
        this.products.controls.forEach((control) => {
            const bomLinkNameControl = control.get('bomLinkName');
            const baseLinkName = this.extractBaseLinkName(bomLinkNameControl.value);
            bomLinkNameControl.setValue(baseLinkName);
        });
    }

    private extractBaseLinkName(linkName: string): string {
        if (!linkName) {
            return '';
        }

        const linkSuffix = '_link';
        const linkIndex = linkName.lastIndexOf(linkSuffix);

        if (linkIndex === -1) {
            // If '_link' is not found, return the original name
            return linkName;
        }

        // Extract everything up to and including '_link'
        return linkName.substring(0, linkIndex + linkSuffix.length);
    }

    private addLinkNamePagination(names: string[]): string[] {
        const namesCounter = new Map<string, number>();
        const newNames: string[] = [];
        names.forEach((_, index) => {
            const currentName: string = names[index];
            if (namesCounter.has(currentName)) {
                namesCounter.set(currentName, namesCounter.get(currentName) + 1);
            } else {
                namesCounter.set(currentName, 1);
            }
            newNames.push(currentName + `_${String(namesCounter.get(currentName))}`);
        });

        const singleNames: string[] = this.searchSingleNames(namesCounter);
        names.forEach((name, i) => {
            if (singleNames.includes(names[i])) {
                newNames[i] = names[i];
            }
        });

        return newNames;
    }

    private searchSingleNames(namesCounter: Map<string, number>): string[] {
        const singleNames: string[] = [];
        namesCounter.forEach((value, key, map) => {
            if (map.get(key) === 1) {
                singleNames.push(key);
            }
        });

        return singleNames;
    }

    private extractLinkNames(): string[] {
        return this.products.controls.map((control) => {
            const usedProd = control.get('usedProd')?.value;
            const bomLinkName = control.get('bomLinkName')?.value || '';
            return isDefined(usedProd) ? bomLinkName : '';
        });
    }

    private updateFormControlsWithPaginatedNames(paginatedLinkNames: string[]): void {
        this.products.controls.forEach((control, index) => {
            const usedProd = control.get('usedProd')?.value;
            const linkName = isDefined(usedProd) ? paginatedLinkNames[index] : '';
            control.get('bomLinkName')?.setValue(linkName);
        });
    }

    private createSelectedProductsDataArray(paginatedLinkNames: string[]): ExtendedProductdata[] {
        const selectedProducts: ExtendedProductdata[] = [];
        this.products.controls.forEach((control, index) => {
            const usedProd = control.get('usedProd')?.value;
            if (isDefined(usedProd)) {
                selectedProducts.push(this.createProductData(control, paginatedLinkNames[index]));
            }
        });

        return selectedProducts;
    }

    private createProductData(control: AbstractControl, bomLinkName: string): ExtendedProductdata {
        const usedProd = control.get('usedProd')?.value;
        return {
            usedProd,
            usedProdTU: control.get('usedProdTU')?.value ?? null,
            usedAnt0: control.get('usedAnt0')?.value ?? null,
            usedAnt1: control.get('usedAnt1')?.value ?? null,
            bomLinkName,
            threshold: this.isMhProduct(usedProd) ? 350 : 0
        };
    }

    private updateLogicalTopologyData(selectedProducts: ExtendedProductdata[]): void {
        this.logicalTopology.selectedProductsPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] = selectedProducts;
    }

    private emitFormValidityChange(): void {
        this.productsIsValidChange.emit(this.editForm.valid);
    }

    private resetProductChangedFlag(): void {
        if (this.isProductChanged) {
            this.isProductChanged = false;
        }
    }

    private checkMhProduct() {
        this.hasMhProduct = this.products.controls.some((selectedProduct: FormGroup) => {
            const usedProduct = selectedProduct.get('usedProd').value;
            if (usedProduct) {
                return this.engineRadioList.find((radio) => radio.id === usedProduct).productType === ProductTypeEnum.MH;
            }
            return false;
        });
    }

    private setProductForInfoButton() {
        const usedBuId = this.logicalTopology.defaultBuProductPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX];
        this.defaultBUName = this.availableBu.length ? this.availableBu.find((bu) => bu.key === usedBuId).value : '';

        const usedTuId = this.logicalTopology.defaultTuProductPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX];
        this.defaultTUName = this.availableTu.length ? this.availableTu.find((bu) => bu.key === usedTuId).value : '';
    }

    private updateSecondProductList(radio: EngineProductDTO) {
        this.secondLinkProductsList[radio.id] = [];
        if (radio.isBU && radio.productType === ProductTypeEnum.TG_MH) {
            this.secondLinkProductsList[radio.id] = this.engineRadioList.filter(
                (enRadio: EngineProductDTO) => enRadio.productType === ProductTypeEnum.TG_MH && !enRadio.isPtP
            );
        }
    }

    private getProductDataList(selectedProducts: ExtendedProductdata[], requiredCount: number): ExtendedProductdata[] {
        const result = [...selectedProducts];
        while (result.length < requiredCount) {
            result.push({
                usedProd: null,
                usedProdTU: null,
                usedAnt0: null,
                usedAnt1: null,
                bomLinkName: '',
                threshold: 0
            });
        }

        return result;
    }
}
